/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ID } from '@gh/common-shared-data';
import { AbstractRestService, arrayOf, identity, RestSettings } from '@gh/core-data';
import { Observable } from 'rxjs';
import { TakeOffParamDto, TakeOffParamMapper } from '../take-off-params';
import { GraphicalTakeOffTopDto, GraphicalTakeOffTopViewDto } from './dto';
import { GraphicalTakeOffTopMapper, GraphicalTakeOffTopViewMapper } from './top.mapper';
import { getGTOEndpoint } from './utils';

/**
 * TOP REST service
 */
@Injectable()
export class GraphicalTakeOffTopRestService extends AbstractRestService {
  /**
   * Constructor
   *
   * @param {RestSettings} settings - REST settings
   * @returns {void}
   */
  constructor(settings: RestSettings) {
    super(settings);
  }

  /**
   * Fetch all TOPs by house plan id
   *
   * @param {ID} hpId - House plan id
   * @param {string} entryPoint - Entry point
   * @param {string} takeOffParamId - TOP id
   * @returns {Observable<GraphicalTakeOffTopDto[]>} - List of house plan TOPs
   */
  findAll(hpId: ID, viewIdName: string, viewId: ID, entryPoint: string, takeOffParamId?: string):
    Observable<GraphicalTakeOffTopDto[]> {
    const url = getGTOEndpoint(`${hpId}/#-take-off-params?entryPoint=${entryPoint}`, entryPoint);
    const params = {
      takeOffParamId,
    };

    params[viewIdName] = viewId;
    return this.httpGet(url, params, void 0, arrayOf(GraphicalTakeOffTopMapper));
  }

  /**
   * Fetch all TOPs by house plan view id
   *
   * @param {ID} hpId - House plan id
   * @param {ID} hpViewId - House plan view id
   * @param {string} entryPoint - Entry point
   * @returns {Observable<GraphicalTakeOffTopDto[]>} - List of house plan view TOPs
   */
  findAllView(hpId: ID, hpViewId: ID, entryPoint: string): Observable<GraphicalTakeOffTopViewDto[]> {
    return this.httpGet(
      getGTOEndpoint(`${hpId}/#-views/${hpViewId}/take-off-params?entryPoint=${entryPoint}`, entryPoint),
      arrayOf(GraphicalTakeOffTopViewMapper),
    );
  }

  /**
   * Get house plan view TOP by id
   *
   * @param {ID} hpId - House plan id
   * @param {ID} topId - TOP id
   * @param {string} entryPoint - Entry point
   * @returns {Observable<GtoHousePlanViewDto>} - House plan view TOP
   */
  get(hpId: ID, topId: ID, entryPoint: string): Observable<GraphicalTakeOffTopDto> {
    return this.httpGet(
      getGTOEndpoint(`${hpId}/#-take-off-params/${topId}?entryPoint=${entryPoint}`, entryPoint),
      GraphicalTakeOffTopMapper,
    );
  }

  /**
   * Create new TOP
   *
   * @param {ID} hpId
   * @param {TakeOffParam} topData
   * @param {string} entryPoint
   * @returns {Observable<GraphicalTakeOffTopDto>}
   */
  create(hpId: ID, topData: TakeOffParamDto, entryPoint: string): Observable<GraphicalTakeOffTopDto> {
    return this.httpPost(
      getGTOEndpoint(`${hpId}/#-take-off-params?entryPoint=${entryPoint}`, entryPoint),
      <any>topData,
      TakeOffParamMapper,
      GraphicalTakeOffTopMapper,
    );
  }

  bulkDelete(items: any[]): Observable<any> {
    return this.httpDelete(`${this.bp}/take-off-params`, items, identity());
  }
}
